import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { supabase } from './supabase';
import { getConversations, deleteConversation } from './services/chatHistoryService';
import { ChatConversation } from './types';

const STREAM_ICONS: Record<string, string> = {
    'Mathematics': 'calculate',
    'Bio Science': 'biotech',
    'Commerce': 'finance_mode',
    'Arts': 'palette',
    'Technology': 'memory',
};

function formatDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFullDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

export default function ChatHistoryPage() {
    const navigate = useNavigate();
    const [isCompactLayout, setIsCompactLayout] = useState(() => window.innerWidth < 900);
    const [userId, setUserId] = useState('');
    const [loading, setLoading] = useState(true);
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStream, setFilterStream] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);

    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { navigate('/student-login'); return; }
            const { data: student } = await supabase.from('students').select('id').eq('user_id', session.user.id).maybeSingle();
            if (!student) { navigate('/student-login'); return; }
            setUserId(session.user.id);
        };
        checkAuth();
    }, [navigate]);

    useEffect(() => {
        const handleResize = () => setIsCompactLayout(window.innerWidth < 900);

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadConversations = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError('');
            const convos = await getConversations(userId);
            setConversations(convos);
        } catch (err: any) {
            console.error('Failed to load conversations:', err);
            setError(err?.message || 'Failed to load conversations. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    const handleDelete = async (id: string) => {
        try {
            await deleteConversation(id);
            setConversations(prev => prev.filter(c => c.id !== id));
            setDeleteConfirmId(null);
            if (expandedId === id) setExpandedId(null);
        } catch (err) {
            console.error('Failed to delete conversation:', err);
        }
    };

    const streams = Array.from(new Set(conversations.map(c => c.stream).filter(Boolean)));

    const filtered = conversations.filter(c => {
        const matchesSearch = !searchQuery ||
            c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.messages.some(m => m.text.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesStream = !filterStream || c.stream === filterStream;
        return matchesSearch && matchesStream;
    });

    const expandedConvo = expandedId ? conversations.find(c => c.id === expandedId) : null;
    const showSplitView = !!expandedConvo && !isCompactLayout;

    if (loading && !conversations.length) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTopColor: '#197fe6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
                    <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading chat history...</p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fbff 0%, #f8fafc 36%, #ffffff 100%)', padding: isCompactLayout ? '1rem 0.85rem 5.5rem' : 'clamp(1.25rem, 4vw, 3rem) clamp(0.75rem, 3vw, 3rem)' }}>
            {/* Inline styles for markdown content */}
            <style>{`
                .ch-ai-content { line-height: 1.7; }
                .ch-ai-content pre { background: #1e293b; color: #e2e8f0; padding: 0.75rem; border-radius: 10px; overflow-x: auto; font-size: 0.82rem; }
                .ch-ai-content code { background: #e2e8f0; padding: 1px 5px; border-radius: 4px; font-size: 0.83em; color: #197fe6; }
                .ch-ai-content pre code { background: none; color: inherit; padding: 0; }
            `}</style>

            <div style={{ maxWidth: '960px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '2rem' }}>
                    <span style={{ display: 'inline-block', background: 'rgba(25,127,230,0.1)', color: '#197fe6', padding: '0.35rem 0.9rem', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: '0.75rem' }}>Chat History</span>
                    <div style={{ display: 'flex', alignItems: isCompactLayout ? 'stretch' : 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: isCompactLayout ? 'flex-start' : 'center', gap: '1rem', width: isCompactLayout ? '100%' : 'auto' }}>
                            <button
                                onClick={() => navigate('/student-dashboard')}
                                style={{ background: 'none', border: '1px solid rgba(0,0,0,0.1)', padding: '0.5rem 0.9rem', borderRadius: '10px', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                                ← Back
                            </button>
                            <div>
                                <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, color: '#0f172a', margin: 0 }}>Chat History</h1>
                                <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                        <Link to="/ai-assistant" style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.25rem',
                            background: 'linear-gradient(135deg, #197fe6, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '10px',
                            fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
                            boxShadow: '0 4px 12px rgba(25,127,230,0.25)',
                            justifyContent: 'center',
                            width: isCompactLayout ? '100%' : 'auto',
                        }}>
                            + New Chat
                        </Link>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div style={{ background: 'rgba(244,63,94,0.06)', border: '1px solid rgba(244,63,94,0.2)', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                        <p style={{ margin: 0, color: '#e11d48', fontSize: '0.85rem' }}>{error}</p>
                        <button onClick={loadConversations} style={{ background: '#fff', border: '1px solid rgba(244,63,94,0.3)', color: '#e11d48', padding: '0.4rem 1rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Retry</button>
                    </div>
                )}

                {/* Filters */}
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, background: 'rgba(255,255,255,0.94)', padding: isCompactLayout ? '1rem' : '1rem 1.1rem', borderRadius: '18px', border: '1px solid rgba(148,163,184,0.14)', boxShadow: '0 12px 32px rgba(15,23,42,0.04)' }}>
                    <div style={{ flex: 1, minWidth: isCompactLayout ? '100%' : '200px', position: 'relative' as const, display: 'flex', alignItems: 'center' }}>
                        <span className="material-symbols-outlined" style={{ position: 'absolute' as const, left: '0.9rem', fontSize: '1rem', color: '#94a3b8', pointerEvents: 'none' as const }}>search</span>
                        <input
                            type="text"
                            id="chat-history-search"
                            name="chatHistorySearch"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: '100%', padding: '0.7rem 2.5rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '10px', background: '#fff', fontSize: '0.875rem', fontFamily: 'inherit', color: '#0f172a' }}
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} style={{ position: 'absolute' as const, right: '0.7rem', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.9rem', padding: '0.2rem', lineHeight: 1 }}>✕</button>
                        )}
                    </div>
                    <select
                        id="chat-history-stream-filter"
                        name="chatHistoryStreamFilter"
                        value={filterStream}
                        onChange={(e) => setFilterStream(e.target.value)}
                        style={{ padding: '0.7rem 1rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '10px', background: '#fff', fontSize: '0.875rem', fontFamily: 'inherit', color: '#0f172a', cursor: 'pointer', minWidth: isCompactLayout ? '100%' : '150px', width: isCompactLayout ? '100%' : 'auto' }}
                    >
                        <option value="">All Streams</option>
                        {streams.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                {/* Content */}
                {filtered.length === 0 ? (
                    <div style={{ textAlign: 'center' as const, padding: '4rem 2rem', background: '#fff', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <div style={{ width: '72px', height: '72px', margin: '0 auto 1rem', borderRadius: '22px', background: 'rgba(25,127,230,0.08)', display: 'grid', placeItems: 'center', color: '#197fe6' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '2rem' }}>forum</span>
                        </div>
                        <h3 style={{ margin: '0 0 0.5rem', color: '#0f172a', fontWeight: 700, fontSize: '1.15rem' }}>
                            {searchQuery || filterStream ? 'No matching conversations' : 'No conversations yet'}
                        </h3>
                        <p style={{ margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.9rem' }}>
                            {searchQuery || filterStream
                                ? 'Try adjusting your search or filter'
                                : 'Start a new chat with the AI Assistant to begin learning'}
                        </p>
                        {!searchQuery && !filterStream && (
                            <Link to="/ai-assistant" style={{
                                display: 'inline-flex', padding: '0.7rem 1.5rem',
                                background: 'linear-gradient(135deg, #197fe6, #8b5cf6)', color: '#fff',
                                borderRadius: '10px', textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem',
                            }}>Start Your First Chat</Link>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: showSplitView ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr', gap: '1.5rem' }}>
                        {/* Conversation List */}
                        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
                            {filtered.map(convo => {
                                const isActive = expandedId === convo.id;
                                const isHovered = hoveredCard === convo.id;
                                return (
                                    <div
                                        key={convo.id}
                                        onClick={() => setExpandedId(isActive ? null : convo.id)}
                                        onMouseEnter={() => setHoveredCard(convo.id)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        style={{
                                            background: isActive ? 'rgba(25,127,230,0.02)' : '#fff',
                                            border: isActive ? '1px solid #197fe6' : isHovered ? '1px solid rgba(25,127,230,0.2)' : '1px solid rgba(0,0,0,0.06)',
                                            borderRadius: '20px',
                                            padding: isCompactLayout ? '1rem' : '1rem 1.25rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            position: 'relative' as const,
                                            boxShadow: isActive ? '0 4px 16px rgba(25,127,230,0.15)' : isHovered ? '0 4px 16px rgba(0,0,0,0.06)' : '0 2px 12px rgba(0,0,0,0.04)',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: '1.2rem', color: '#197fe6' }}>{STREAM_ICONS[convo.stream] || 'forum'}</span>
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isCompactLayout ? 'normal' as const : 'nowrap' as const }}>{convo.title}</h4>
                                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' as const }}>
                                                    <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '0.2rem 0.5rem', borderRadius: '6px', background: 'rgba(25,127,230,0.08)', color: '#197fe6' }}>{convo.stream}</span>
                                                    <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '0.2rem 0.5rem', borderRadius: '6px', background: 'rgba(16,185,129,0.08)', color: '#059669' }}>{convo.subject}</span>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: isCompactLayout ? 'left' as const : 'right' as const, flexShrink: 0 }}>
                                                <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>{formatDate(convo.lastMessageAt)}</span>
                                                <span style={{ display: 'block', fontSize: '0.7rem', color: '#cbd5e1', marginTop: '0.25rem' }}>{convo.messages.length} msg{convo.messages.length !== 1 ? 's' : ''}</span>
                                            </div>
                                        </div>

                                        {convo.pdfFileName && (
                                            <div style={{ marginTop: '0.6rem', padding: '0.35rem 0.6rem', background: 'rgba(25,127,230,0.05)', borderRadius: '6px', fontSize: '0.75rem', color: '#197fe6', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                                                PDF: {convo.pdfFileName}
                                            </div>
                                        )}

                                        {/* Delete button */}
                                        <div style={isCompactLayout ? { marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' } : { position: 'absolute' as const, bottom: '0.75rem', right: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                                            {deleteConfirmId === convo.id ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                                                    <span>Delete?</span>
                                                    <button onClick={() => handleDelete(convo.id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Yes</button>
                                                    <button onClick={() => setDeleteConfirmId(null)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>No</button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setDeleteConfirmId(convo.id)}
                                                    title="Delete conversation"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem', opacity: isHovered ? 0.7 : 0.4, transition: 'opacity 0.2s' }}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Expanded Conversation Detail */}
                        {expandedConvo && (
                            <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '20px', display: 'flex', flexDirection: 'column' as const, maxHeight: isCompactLayout ? '70dvh' : '75vh', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                                {/* Detail Header */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: isCompactLayout ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid rgba(0,0,0,0.06)', gap: '1rem' }}>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{expandedConvo.subject}</h3>
                                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{formatFullDate(expandedConvo.createdAt)}</p>
                                    </div>
                                    <button
                                        onClick={() => setExpandedId(null)}
                                        style={{ background: 'none', border: '1px solid rgba(0,0,0,0.1)', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', color: '#94a3b8', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Messages */}
                                <div style={{ flex: 1, overflowY: 'auto' as const, padding: isCompactLayout ? '1rem' : '1.25rem 1.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
                                    {expandedConvo.messages.length === 0 && (
                                        <p style={{ textAlign: 'center' as const, color: '#94a3b8', fontSize: '0.9rem', padding: '2rem 0' }}>No messages in this conversation.</p>
                                    )}
                                    {expandedConvo.messages.map((msg, idx) => (
                                        <div key={idx} style={{ display: 'flex', flexDirection: 'column' as const, alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.35rem', padding: '0 0.25rem' }}>
                                                {msg.role === 'user' ? 'You' : 'AI'}
                                            </div>
                                            <div style={{
                                                maxWidth: isCompactLayout ? '100%' : '85%', padding: '0.85rem 1.1rem', borderRadius: '14px', fontSize: '0.88rem', lineHeight: 1.65,
                                                wordWrap: 'break-word' as const, overflowWrap: 'break-word' as const,
                                                ...(msg.role === 'user'
                                                    ? { background: 'linear-gradient(135deg, #197fe6, #8b5cf6)', color: '#fff', borderBottomRightRadius: '4px' }
                                                    : { background: '#f8fafc', border: '1px solid rgba(0,0,0,0.06)', color: '#1e293b', borderBottomLeftRadius: '4px' }
                                                ),
                                            }}>
                                                {msg.role === 'model' ? (
                                                    <div className="ch-ai-content">
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkMath]}
                                                            rehypePlugins={[rehypeKatex]}
                                                        >
                                                            {msg.text}
                                                        </ReactMarkdown>
                                                    </div>
                                                ) : (
                                                    <span>{msg.text}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Footer */}
                                <div style={{ padding: isCompactLayout ? '1rem' : '1rem 1.5rem', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Link to="/ai-assistant" style={{
                                        display: 'block', textAlign: 'center' as const, padding: '0.7rem',
                                        background: 'rgba(25,127,230,0.06)', color: '#197fe6', borderRadius: '10px',
                                        textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem',
                                    }}>
                                        Continue in AI Assistant →
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
